import Router from "next/router";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { IntlShape } from "react-intl";
import { useDispatch } from "react-redux";

import {
  startUniversalLoading,
  stopUniversalLoading,
} from "shared/data/actions/loader";
import { BaggageLimit } from "shared/gol-api/types/flightSearch";
import { getBaggageConditions } from "shared/lib/golCopy";
import {
  BaggageDetail,
  SegmentBaggage,
} from "shared/lib/golObjectTypes/BaggageDetails/BaggageDetails";
import { ResponseObject } from "shared/lib/golObjectTypes/ResponseObject";
import Logger from "shared/services/Logger";

import { GetRefsForFlightOffer } from "@components/Flights/FlightAdditionalInfo";
import FormattedMessage from "@components/UI/FormattedMessage";
import Modal from "@components/UI/Modal";
import Switch from "@components/UI/Switch";

import Footer from "./Footer";
import BaggageRow from "./Row";

type Props = {
  intl: IntlShape;
  onClose: () => void;
  getRefsForFlightOffer: GetRefsForFlightOffer;
  baggageInfo: BaggageLimit;
};

export type Column = {
  label: string;
  width: number;
};

const AdditionalBaggageModal = ({
  intl,
  onClose,
  getRefsForFlightOffer,
  baggageInfo,
}: Props) => {
  const dispatch = useDispatch();

  const [isLoading, setLoading] = useState(true);
  const [data, setData] = useState<BaggageDetail[]>([]);
  const [unit, setUnit] = useState<"KILOGRAMS" | "POUNDS">("KILOGRAMS");

  /* eslint-disable react-hooks/exhaustive-deps */
  const onMountFetch = useCallback(async () => {
    dispatch(startUniversalLoading());
    const refs = getRefsForFlightOffer();

    if (
      !baggageInfo.DetailsAvailable &&
      baggageInfo.BaggageSelectionAvailable
    ) {
      dispatch(stopUniversalLoading());
      setLoading(false);
      return;
    }

    const baggageResponse: ResponseObject = await getBaggageConditions(
      refs,
      Router.router.query.currency
    );

    if (!baggageResponse.success) {
      dispatch(stopUniversalLoading());
      setLoading(false);
      Logger.error("Baggage details couldn't be loaded.", refs);
      return;
    }

    setData([...baggageResponse.data.BaggageDetails.BaggageDetail]);
    dispatch(stopUniversalLoading());
    setLoading(false);
  }, [dispatch]);

  useEffect(() => {
    onMountFetch();
  }, [onMountFetch]);

  const columns: Column[] = useMemo(
    () => [
      {
        label: intl.formatMessage({
          id: "FlightAdditionalInfoBaggage.from",
        }),
        width: 120,
      },
      {
        label: intl.formatMessage({
          id: "FlightAdditionalInfoBaggage.to",
        }),
        width: 120,
      },
      {
        label: intl.formatMessage({
          id: "FlightAdditionalInfoBaggage.baggage",
        }),
        width: 210,
      },
      {
        label: intl.formatMessage({
          id: "FlightAdditionalInfoBaggage.anotherBaggage",
        }),
        width: 240,
      },
      {
        label: intl.formatMessage({
          id: "FlightAdditionalInfoBaggage.handBaggage",
        }),
        width: 150,
      },
      {
        label: intl.formatMessage({
          id: "FlightAdditionalInfoBaggage.link",
        }),
        width: 40,
      },
    ],
    [intl]
  );

  const baggageItems = useMemo(() => {
    const body = {
      passenger: [],
      infant: [],
    };

    let infantDisplayed = false;
    let passengerDisplayed = false;

    /* eslint-disable */
    for (const segment of data) {
      segment.SegmentBaggage.forEach(
        (segmentBaggage: SegmentBaggage, index: number) => {
          if (segmentBaggage.BaggageItem === undefined) {
            return;
          }

          if (!passengerDisplayed && segment.PassengerType !== "INF") {
            if (index === segment.SegmentBaggage.length - 1) {
              passengerDisplayed = true;
            }

            body.passenger.push(
              <BaggageRow
                intl={intl}
                key={index}
                data={segmentBaggage}
                unit={unit}
                columns={columns}
              />
            );
          } else if (segment.PassengerType === "INF" && !infantDisplayed) {
            if (index === segment.SegmentBaggage.length - 1) {
              infantDisplayed = true;
            }
            body.infant.push(
              <BaggageRow
                intl={intl}
                key={index}
                data={segmentBaggage}
                unit={unit}
                columns={columns}
              />
            );
          }
        }
      );

      if (infantDisplayed && passengerDisplayed) {
        break;
      }
    }

    return body;
  }, [data, unit, columns, intl]);

  if (isLoading) {
    return null;
  }

  const isEmpty = !data.length;

  const detailInNextStep =
    baggageInfo.DetailsAvailable === "false" &&
    baggageInfo.BaggageSelectionAvailable === "true";

  const onSwitchChange = (item: string) => {
    setUnit(item === "kg" ? "KILOGRAMS" : "POUNDS");
  };

  return (
    <Modal isActive onClose={onClose}>
      <div
        className="flight-additional-info-baggage-modal-content"
        style={
          isEmpty
            ? {
                textAlign: "center",
              }
            : {}
        }
      >
        {isEmpty ? (
          detailInNextStep ? (
            <FormattedMessage id="FlightAdditionalInfoBaggage.baggageNextStepInfo" />
          ) : (
            <FormattedMessage id="FlightAdditionalInfoBaggage.noDataBaggage" />
          )
        ) : (
          <>
            <div className="flight-additional-info-baggage-modal-header-wrap">
              <h3 className="flight-additional-info-baggage-modal-header">
                <FormattedMessage id="FlightAdditionalInfoBaggage.allowedBaggage" />
              </h3>
              <Switch items={["kg", "lb"]} onChange={onSwitchChange} />
            </div>
            <div className="flight-additional-info-baggage-modal-table">
              <div className="flight-additional-info-baggage-modal-table-header">
                {columns.map((column, index) => (
                  <div
                    key={index}
                    className="flight-additional-info-baggage-modal-table-header-column"
                    style={{ width: column.width }}
                  >
                    {column.label}
                  </div>
                ))}
              </div>
              {baggageItems.passenger}
              {!!baggageItems.infant.length && (
                <>
                  <div className="flight-additional-info-baggage-modal-infantheader">
                    <FormattedMessage id="FlightAdditionalInfoBaggage.infantException" />
                  </div>
                  {baggageItems.infant}
                </>
              )}
            </div>
            <Footer />
          </>
        )}
      </div>
    </Modal>
  );
};

export default AdditionalBaggageModal;
